import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, DollarSign, Clock, Sparkles, TrendingUp, Wallet, Receipt, PiggyBank, Target, AlertCircle } from "lucide-react";
import api, { money } from "@/lib/api";
import localApi from "@/lib/localApi";
import Layout from "@/components/Layout";
import JobBoardPanel from "@/components/JobBoardPanel";
import { InviteMechanic } from "@/components/InviteMechanic";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

function Stat({ icon: Icon, label, value, accent, sub, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-800 bg-slate-900 p-5 transition-transform duration-200 hover:-translate-y-[2px]">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{label}</span>
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${accent}`}><Icon className="h-4 w-4" /></div>
      </div>
      <p className="font-head font-black text-3xl text-slate-50 mt-4 tabular-nums">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [bids, setBids] = useState(null);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setDash(data || {})).catch(() => setDash({}));
    localApi.get("/marketplace/my-bids").then(({ data }) => setBids(data?.active_count ?? 0)).catch(() => setBids(0));
    localApi.get("/marketplace/subscription").then(({ data }) => setSub(data)).catch(() => {});
  }, []);

  const d = dash || {};
  const val = (x) => (dash === null ? "—" : money(x || 0));

  const trialing = sub?.status === "trialing";
  const expired = sub?.status === "expired";

  return (
    <Layout
      title={`Welcome, ${user?.name?.split(" ")[0] || "there"}`}
      action={
        <div className="flex gap-2">
          <InviteMechanic shopName={user?.business_name || user?.name} />
          <Button data-testid="dashboard-ai-button" onClick={() => navigate("/ai")} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
            <Sparkles className="h-4 w-4 mr-1.5" /> AI Assistant
          </Button>
        </div>
      }
    >
      <div className="space-y-6 animate-fade-up">
        {/* Subscription nudge */}
        {sub?.is_mechanic && (trialing || expired) && (
          <div className={`rounded-xl border p-4 flex items-center gap-3 ${expired ? "border-rose-500/30 bg-rose-500/5" : "border-amber-500/30 bg-amber-500/5"}`} data-testid="dashboard-sub-banner">
            <Clock className={`h-5 w-5 ${expired ? "text-rose-400" : "text-amber-400"}`} />
            <p className="text-sm text-slate-300 flex-1">
              {expired ? "Your free trial has ended. Subscribe to keep receiving and claiming jobs." : `You're on the Pitcrew Pro free trial — ${sub?.days_left} days left.`}
            </p>
            <Button data-testid="dashboard-sub-cta" onClick={() => navigate("/billing")} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold shrink-0">
              {expired ? "Subscribe" : "View plan"}
            </Button>
          </div>
        )}

        {/* Financial Overview */}
        <div>
          <h2 className="font-head font-bold text-lg text-slate-100 mb-3">Financial Overview</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat testid="stat-revenue-month" icon={DollarSign} label="Revenue · This Month"
              value={val(d.revenue_month)} accent="bg-emerald-500/15 text-emerald-400" sub="Invoices marked paid" />
            <Stat testid="stat-profit-month" icon={PiggyBank} label="Net Profit · This Month"
              value={val(d.profit_month)} accent="bg-emerald-500/15 text-emerald-400" sub="Revenue minus expenses" />
            <Stat testid="stat-expenses-month" icon={Receipt} label="Expenses · This Month"
              value={val(d.expenses_month)} accent="bg-rose-500/15 text-rose-400" sub="Parts, supplies & overhead" />
            <Stat testid="stat-revenue-total" icon={TrendingUp} label="Total Revenue"
              value={val(d.revenue_total)} accent="bg-blue-500/15 text-blue-400" sub="All-time paid invoices" />
          </div>
        </div>

        {/* Pipeline & Marketplace */}
        <div>
          <h2 className="font-head font-bold text-lg text-slate-100 mb-3">Pipeline &amp; Leads</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat testid="stat-potential-revenue" icon={Target} label="Potential Revenue"
              value={val(d.potential_revenue)} accent="bg-amber-500/15 text-amber-400"
              sub={dash === null ? "" : `${d.open_estimate_count ?? 0} open estimate${(d.open_estimate_count ?? 0) === 1 ? "" : "s"}`} />
            <Stat testid="stat-outstanding" icon={AlertCircle} label="Outstanding"
              value={val(d.outstanding)} accent="bg-orange-500/15 text-orange-400"
              sub={dash === null ? "" : `${d.unpaid_count ?? 0} unpaid invoice${(d.unpaid_count ?? 0) === 1 ? "" : "s"}`} />
            <Stat testid="stat-win-rate" icon={Wallet} label="Estimate Win Rate"
              value={dash === null ? "—" : `${d.win_rate ?? 0}%`} accent="bg-blue-500/15 text-blue-400"
              sub={dash === null ? "" : `${d.estimates_approved_count ?? 0} approved`} />
            <Stat testid="stat-my-bids" icon={FileText} label="Active Bids"
              value={bids ?? "—"} accent="bg-blue-500/15 text-blue-400" sub="Estimates out to clients" />
          </div>
        </div>

        {/* Job Board — the central feature */}
        <div className="pt-2">
          <JobBoardPanel />
        </div>
      </div>
    </Layout>
  );
}
